<template>
  <div class="col-sm-12">
    <div class="card shadow-sm p-3">
      <!-- Filters Section -->
      <div class="mb-3">
        <div class="row">
          <!-- Distributor Dropdown -->
          <div class="col-md-3 mb-3">
            <label for="distributor" class="form-label fw-semibold">Distributor</label>
            <select
              v-model="filters.distributor"
              id="distributor"
              class="form-select"
              @change="onDistributorChange"
            >
              <option value="">All</option>
              <option
                v-for="dist in distributors"
                :key="dist.profile_id ?? dist.id"
                :value="dist.profile_id ?? dist.id"
              >
                {{ dist.name ?? dist.first_name ?? dist.full_name ?? 'Unknown' }}
              </option>
            </select>
          </div>

          <!-- Date Field -->
          <div class="col-md-3 mb-3">
            <label for="date" class="form-label fw-semibold">Date</label>
            <input type="date" v-model="filters.date" id="date" class="form-control" />
          </div>

          <!-- Vehicle Dropdown -->
        
          <!-- Search Button -->
          <div class="col-md-3 d-flex align-items-end mb-3">
            <button class="btn btn-primary w-100" @click="search" :disabled="loading">
              <i class="fa fa-search me-1" :class="{ 'fa-spin': loading }"></i>
              {{ loading ? 'Searching...' : 'Search' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Loader -->
      <div v-if="loading" class="text-center my-4">
        <i class="fa fa-spinner fa-spin fa-2x text-primary"></i>
        <p class="mt-2">Loading report data...</p>
      </div>

      <!-- Results Table -->
      <div v-if="!loading && reportData.length > 0" class="table-responsive mt-3">
        <table class="table table-bordered table-hover align-middle">
          <thead class="table-light">
            <tr>
              <th class="text-center">#</th>
              <th>Dealer Name</th>
              <th>Invoice No</th>
              <th v-for="item in uniqueItems" :key="item.key" class="text-center">
                {{ item.name }}
              </th>
              <th class="text-end">Cash Amount</th>
              <th class="text-end">Cheque Amount</th>
              <th class="text-end">Bank Transfer</th>
              <th class="text-end">Credit Amount</th>
              <th class="text-end">Invoice Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in reportData" :key="index">
              <td class="text-center">{{ index + 1 }}</td>
              <td>{{ row.dealer_name }}</td>
              <td>{{ row.invoice_no }}</td>

              <td v-for="item in uniqueItems" :key="item.key" class="text-center">
                {{ getItemQuantity(row, item.key) }}
              </td>

              <td class="text-end">{{ formatCurrency(row.cash_amount) }}</td>
              <td class="text-end">{{ formatCurrency(row.cheque_amount) }}</td>
              <td class="text-end">{{ formatCurrency(row.bank_transfer_amount) }}</td>
              <td class="text-end">{{ formatCurrency(row.credit_amount) }}</td>
              <td class="text-end fw-bold">{{ formatCurrency(row.invoice_total) }}</td>
            </tr>
          </tbody>

          <!-- Totals Row -->
          <tfoot class="table-secondary fw-bold">
            <tr>
              <td colspan="3" class="text-end"><strong>Grand Totals:</strong></td>
              <td v-for="item in uniqueItems" :key="item.key" class="text-center">
                <strong>{{ getTotalForItem(item.key) }}</strong>
              </td>
              <td class="text-end">{{ formatCurrency(totals.total_cash) }}</td>
              <td class="text-end">{{ formatCurrency(totals.total_cheque) }}</td>
              <td class="text-end">{{ formatCurrency(totals.total_bank_transfer) }}</td>
              <td class="text-end">{{ formatCurrency(totals.total_credit) }}</td>
              <td class="text-end">{{ formatCurrency(totals.total_invoice_amount) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- No Data Message -->
      <div v-else-if="!loading && searched" class="text-center mt-4">
        <i class="fa fa-inbox fa-3x text-muted mb-3"></i>
        <p class="text-muted">No data found for the selected filters.</p>
      </div>

      <!-- Export Button -->
      <div v-if="!loading && reportData.length > 0" class="mt-3">
        <button class="btn btn-success" @click="exportToExcel">
          <i class="fa fa-download me-1"></i> Export to Excel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchDayEndReport, fetchDistributorsList, fetchVehicleListrep } from "@/services/dataService";

export default {
  name: "DayEndReport",
  data() {
    return {
      profile_id: localStorage.getItem("profile_id") || null,
      filters: {
        distributor: "",
        date: this.getDefaultDate(),
        vehicle: "",
      },
      distributors: [],
      vehicles: [],
      reportData: [],
      uniqueItems: [],
      totals: {
        total_invoices: 0,
        total_invoice_amount: 0,
        total_cash: 0,
        total_cheque: 0,
        total_bank_transfer: 0,
        total_credit: 0,
      },
      loading: false,
      vehiclesLoading: false,
      searched: false,
    };
  },
  created() {
    this.loadDistributors();
  },
  methods: {
    getDefaultDate() {
      const today = new Date();
      return today.toISOString().split("T")[0];
    },

    /**
     * Converts item name to backend key format
     * Backend uses exact lowercase with underscores, preserving numbers and special chars
     */
    convertToBackendKey(itemName) {
      return itemName
        .toLowerCase()
        .replace(/ /g, "_")
        .replace(/\(/g, "")
        .replace(/\)/g, "")
        .replace(/\\/g, "")
        .replace(/"/g, "")
        .replace(/__+/g, "_"); // Replace multiple underscores with single
    },

    async loadDistributors() {
      try {
        const token = localStorage.getItem("token_set");
        const response = await fetchDistributorsList(this.profile_id, token);
        this.distributors = response?.data?.data || [];
      } catch (error) {
        console.error("Error loading distributors:", error);
        this.$toast?.error?.("Failed to load distributors");
      }
    },

    onDistributorChange() {
      this.filters.vehicle = "";
      this.vehicles = [];
      this.reportData = [];
      this.uniqueItems = [];
      this.searched = false;

      if (this.filters.distributor) {
        this.loadVehicles(this.filters.distributor);
      }
    },

    async loadVehicles(distributor_profile_id) {
      this.vehiclesLoading = true;
      this.vehicles = [];
      try {
        const token = localStorage.getItem("token_set");
        const response = await fetchVehicleListrep(distributor_profile_id, token);
        let vehiclesData = response?.data?.data || [];

        this.vehicles = vehiclesData.map(v => ({
          id: v.id ?? v.vehicle_id ?? v.profile_id,
          vehicle_no: v.vehicle_no ?? v.name ?? "Unknown",
        }));
      } catch (error) {
        console.error("Error loading vehicles:", error);
        this.$toast?.error?.("Failed to load vehicles");
      } finally {
        this.vehiclesLoading = false;
      }
    },

    async search() {
      if (!this.filters.distributor) {
        this.$toast?.error?.("Please select a distributor");
        return;
      }

      if (!this.filters.vehicle) {
        this.$toast?.error?.("Please select a vehicle");
        return;
      }

      if (!this.filters.date) {
        this.$toast?.error?.("Please select a date");
        return;
      }

      this.loading = true;
      this.reportData = [];
      this.uniqueItems = [];
      this.searched = false;

      try {
        const token = localStorage.getItem("token_set");

        const params = {
          distributor_profile_id: this.filters.distributor,
          date: this.filters.date,
          vehicle_id: this.filters.vehicle,
        };

        const response = await fetchDayEndReport(token, params);

        if (response?.data?.status) {
          // Convert item names to backend keys
          this.uniqueItems = (response.data.items || []).map(item => ({
            key: this.convertToBackendKey(item.item_name),
            name: item.item_name
          }));

          this.reportData = response.data.data || [];
          
          // Use backend totals but recalculate item totals from actual data
          this.totals = {
            ...response.data.totals,
            total_invoices: this.reportData.length,
            total_invoice_amount: response.data.totals?.total_invoice_amount || 0,
            total_cash: response.data.totals?.total_cash || 0,
            total_cheque: response.data.totals?.total_cheque || 0,
            total_bank_transfer: response.data.totals?.total_bank_transfer || 0,
            total_credit: response.data.totals?.total_credit || 0,
          };

          // Recalculate item totals from actual data (backend totals seem incorrect)
          this.uniqueItems.forEach(item => {
            const totalKey = `total_${item.key}`;
            this.totals[totalKey] = this.reportData.reduce(
              (sum, row) => sum + (parseFloat(row[item.key]) || 0),
              0
            );
          });

          this.searched = true;

          if (!this.reportData.length) {
            this.$toast?.error?.("No data found for the selected criteria");
          } else {
            this.$toast?.success?.(`Report loaded successfully with ${this.reportData.length} invoices`);
          }
        } else {
          this.$toast?.error?.(response?.data?.message || "Failed to load report");
        }
      } catch (error) {
        console.error("Error loading report:", error);
        this.$toast?.error?.("Error loading report. Please try again.");
      } finally {
        this.loading = false;
      }
    },

    getItemQuantity(row, itemKey) {
      const value = row[itemKey] ?? 0;
      return parseFloat(value).toFixed(2);
    },

    formatCurrency(amount) {
      const num = parseFloat(amount) || 0;
      return num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    getTotalForItem(itemKey) {
      const totalKey = `total_${itemKey}`;
      if (this.totals[totalKey] !== undefined) {
        return parseFloat(this.totals[totalKey]).toFixed(2);
      }
      // Fallback: calculate from data
      return this.reportData
        .reduce((sum, row) => sum + (parseFloat(row[itemKey]) || 0), 0)
        .toFixed(2);
    },

    exportToExcel() {
      try {
        // Create header row
        const headers = [
          "#",
          "Dealer Name",
          "Invoice No",
          ...this.uniqueItems.map(item => item.name),
          "Cash Amount",
          "Cheque Amount",
          "Bank Transfer",
          "Credit Amount",
          "Invoice Total",
        ];

        // Create data rows
        const dataRows = this.reportData.map((row, index) => {
          const rowData = [
            index + 1,
            row.dealer_name || '',
            row.invoice_no || '',
          ];
          
          // Add item quantities
          this.uniqueItems.forEach(item => {
            const qty = this.getItemQuantity(row, item.key);
            rowData.push(qty);
          });
          
          // Add monetary values (remove formatting for Excel)
          rowData.push(
            parseFloat(row.cash_amount || 0).toFixed(2),
            parseFloat(row.cheque_amount || 0).toFixed(2),
            parseFloat(row.bank_transfer_amount || 0).toFixed(2),
            parseFloat(row.credit_amount || 0).toFixed(2),
            parseFloat(row.invoice_total || 0).toFixed(2)
          );
          
          return rowData;
        });

        // Create totals row
        const totalsRow = [
          "",
          "",
          "TOTALS",
        ];
        
        // Add item totals
        this.uniqueItems.forEach(item => {
          totalsRow.push(this.getTotalForItem(item.key));
        });
        
        // Add monetary totals
        totalsRow.push(
          parseFloat(this.totals.total_cash || 0).toFixed(2),
          parseFloat(this.totals.total_cheque || 0).toFixed(2),
          parseFloat(this.totals.total_bank_transfer || 0).toFixed(2),
          parseFloat(this.totals.total_credit || 0).toFixed(2),
          parseFloat(this.totals.total_invoice_amount || 0).toFixed(2)
        );

        // Combine all rows
        const allRows = [headers, ...dataRows, totalsRow];

        // Convert to CSV format with proper escaping
        const csvContent = allRows.map(row => {
          return row.map(cell => {
            // Convert to string
            let cellStr = String(cell);
            
            // If cell contains comma, quote, or newline, wrap in quotes and escape quotes
            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
              cellStr = '"' + cellStr.replace(/"/g, '""') + '"';
            }
            
            return cellStr;
          }).join(',');
        }).join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        
        if (navigator.msSaveBlob) { // IE 10+
          navigator.msSaveBlob(blob, `day_end_report_${this.filters.date}.csv`);
        } else {
          const url = URL.createObjectURL(blob);
          link.href = url;
          link.download = `day_end_report_${this.filters.date}.csv`;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }

        this.$toast?.success?.("Report exported successfully");
      } catch (error) {
        console.error("Export error:", error);
        this.$toast?.error?.("Failed to export report. Please try again.");
      }
    },
  }
};
</script>

<style scoped>
.card {
  border-radius: 10px;
}

.form-label {
  font-size: 0.9rem;
}

.table th,
.table td {
  font-size: 0.85rem;
  vertical-align: middle;
}

.table tfoot {
  background-color: #f8f9fa !important;
  font-weight: 600;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

@media print {
  .btn,
  .mb-3 {
    display: none !important;
  }
  
  .table {
    font-size: 0.75rem;
  }
}
</style>